import { useDebounce } from '@/hooks/useDebounce';
import { useAuth } from "@/context/AuthContext";
import { useGlobalLoading } from "@/context/GlobalLoadingContext";
import { useState, useEffect, useRef } from 'react';

export default function TaskSummary({ task, onTaskChange, isLoading }) {
    const [summary, setSummary] = useState(task?.summary || '');
    const debouncedSummary = useDebounce(summary, 1000);
    const { axiosInstance } = useAuth();
    const { showLoading, hideLoading } = useGlobalLoading();
    const pendingSaveRef = useRef(false);

    useEffect(() => {
        if (debouncedSummary && task && debouncedSummary !== task.summary) {
            handleSummaryUpdate(debouncedSummary);
        } else if (pendingSaveRef.current && debouncedSummary === task?.summary) {
            // User typed but value matches original - no save needed
            hideLoading();
            pendingSaveRef.current = false;
        }
    }, [debouncedSummary]);

    const handleSummaryUpdate = async (newSummary) => {
        try {
            await axiosInstance.patch(`/tasks/${task.organization.slug}/${task.key}`, { summary: newSummary });
        } catch (error) {
            console.error('Failed to update summary:', error);
        } finally {
            hideLoading();
            pendingSaveRef.current = false;
        }
    };

    const handleSummaryInput = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        } else {
            const newSummary = e.target.innerText;
            setSummary(newSummary);
            
            // Show loading immediately when user types (if not already showing)
            if (!pendingSaveRef.current && newSummary !== task?.summary) {
                showLoading();
                pendingSaveRef.current = true;
            }
        }
    };

    return (
        <div className="max-w-3xl mx-auto w-full min-h-11">
            {isLoading ? (
                <div className="h-11 flex items-center justify-start">
                    <div className="h-8 bg-muted animate-pulse rounded-md w-2/3" />
                </div>
            ) : (
                <div
                    contentEditable
                    suppressContentEditableWarning
                    onInput={handleSummaryInput}
                    onKeyDown={handleSummaryInput}
                    className="text-2xl font-semibold focus:outline-none py-2 min-h-11"
                    dangerouslySetInnerHTML={{ __html: task?.summary }}
                />
            )}
        </div>
    );
}